<template>
  <div class="school-register-page">
    <div class="topbar">
      <div class="brand">
        <span class="brand-mark">新</span>
        <div>
          <div class="brand-title">新知堂学校开户注册</div>
          <div class="brand-subtitle">资质提交后进入待审核状态</div>
        </div>
      </div>
      <a :href="legacyLoginUrl" class="login-link">已有账号，返回登录</a>
    </div>

    <div class="register-shell">
      <aside class="progress-panel">
        <div class="progress-title">注册资料</div>
        <el-steps :direction="stepsDirection" :active="activeStep" finish-status="success" class="steps">
          <el-step title="账号设置"></el-step>
          <el-step title="证照识别"></el-step>
          <el-step title="学校资料"></el-step>
          <el-step title="联系地址"></el-step>
          <el-step title="提交确认"></el-step>
        </el-steps>
      </aside>

      <main class="form-panel">
        <el-alert
          title="请先上传主体资质证件和办学许可证。OCR 识别仅作为辅助，所有信息均可手工修改。"
          type="info"
          :closable="false"
          show-icon>
        </el-alert>

        <el-form ref="registerForm" :model="form" :rules="rules" label-position="top" class="register-form">
          <section class="form-section">
            <div class="section-head">
              <div>
                <h2>账号设置</h2>
                <p>用于后续登录旧系统，密码按历史兼容规则明文保存。</p>
              </div>
            </div>
            <el-row :gutter="18">
              <el-col :xs="24" :sm="8">
                <el-form-item label="用户名" prop="userName">
                  <el-input v-model="form.userName" maxlength="20" clearable placeholder="4-20位英文或数字" @blur="checkUserName"></el-input>
                  <div v-if="userNameMessage" class="field-hint" :class="{ danger: !userNameAvailable }">{{ userNameMessage }}</div>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="8">
                <el-form-item label="密码" prop="password">
                  <el-input v-model="form.password" maxlength="20" type="password" show-password clearable placeholder="6-20位"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="8">
                <el-form-item label="确认密码" prop="confirmPassword">
                  <el-input v-model="form.confirmPassword" maxlength="20" type="password" show-password clearable placeholder="再次输入密码"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="8">
                <el-form-item label="手机号" prop="mobile">
                  <el-input v-model="form.mobile" maxlength="11" clearable placeholder="用于接收短信验证码"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="16">
                <el-form-item label="短信验证码" prop="smsCode">
                  <div class="code-line">
                    <el-input v-model="form.smsCode" maxlength="6" clearable placeholder="6位验证码"></el-input>
                    <el-button type="primary" plain :disabled="codeCountdown > 0" :loading="codeLoading" @click="sendCode">
                      {{ codeCountdown > 0 ? codeCountdown + 's' : '获取验证码' }}
                    </el-button>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </section>

          <section class="form-section">
            <div class="section-head">
              <div>
                <h2>证照上传</h2>
                <p>支持 JPG、JPEG、PNG，单张不超过 15M。</p>
              </div>
            </div>
            <el-row :gutter="18">
              <el-col :xs="24" :md="12">
                <div class="upload-box" :class="{ filled: files.certpic1.file }">
                  <div class="upload-title">
                    <span>主体资质证件</span>
                    <small>营业执照或民办非企业单位登记证书</small>
                  </div>
                  <div v-if="files.certpic1.preview" class="preview-wrap">
                    <img :src="files.certpic1.preview" alt="主体资质证件预览" />
                    <button type="button" class="remove-file" @click="removeFile('certpic1')">删除</button>
                  </div>
                  <el-upload
                    action=""
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    :auto-upload="false"
                    :show-file-list="false"
                    :on-change="function (file) { handleFileChange('certpic1', file) }">
                    <el-button size="small" icon="el-icon-upload2">选择图片</el-button>
                  </el-upload>
                  <div class="ocr-state">{{ files.certpic1.ocrMessage }}</div>
                </div>
              </el-col>
              <el-col :xs="24" :md="12">
                <div class="upload-box" :class="{ filled: files.certpic2.file }">
                  <div class="upload-title">
                    <span>办学许可证</span>
                    <small>学校办学许可证照片</small>
                  </div>
                  <div v-if="files.certpic2.preview" class="preview-wrap">
                    <img :src="files.certpic2.preview" alt="办学许可证预览" />
                    <button type="button" class="remove-file" @click="removeFile('certpic2')">删除</button>
                  </div>
                  <el-upload
                    action=""
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    :auto-upload="false"
                    :show-file-list="false"
                    :on-change="function (file) { handleFileChange('certpic2', file) }">
                    <el-button size="small" icon="el-icon-upload2">选择图片</el-button>
                  </el-upload>
                  <div class="ocr-state">{{ files.certpic2.ocrMessage }}</div>
                </div>
              </el-col>
            </el-row>

          </section>

          <section class="form-section">
            <div class="section-head">
              <div>
                <h2>学校资料</h2>
                <p>学校名称、法人和地址可由证照识别辅助回填。</p>
              </div>
            </div>
            <el-row :gutter="18">
              <el-col :xs="24" :sm="12">
                <el-form-item label="学校名称" prop="schoolName">
                  <el-input v-model="form.schoolName" maxlength="50" clearable @blur="checkSchoolName"></el-input>
                  <div v-if="schoolNameMessage" class="field-hint" :class="{ danger: !schoolNameAvailable }">{{ schoolNameMessage }}</div>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="法人/负责人" prop="legalEntity">
                  <el-input v-model="form.legalEntity" maxlength="50" clearable></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="学校规模" prop="schoolSize">
                  <el-select v-model="form.schoolSize" placeholder="请选择" class="full-control">
                    <el-option label="50人以下" value="50人以下"></el-option>
                    <el-option label="50-100人" value="50-100人"></el-option>
                    <el-option label="100-300人" value="100-300人"></el-option>
                    <el-option label="300-500人" value="300-500人"></el-option>
                    <el-option label="500人以上" value="500人以上"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="学校官网（选填）" prop="webAddress">
                  <el-input v-model="form.webAddress" maxlength="50" clearable placeholder="可填写学校官网"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="购买渠道一" prop="purchaseChannel1">
                  <el-select v-model="form.purchaseChannel1" placeholder="请选择" class="full-control">
                    <el-option label="学校自购" value="学校自购"></el-option>
                    <el-option label="经销商推荐" value="经销商推荐"></el-option>
                    <el-option label="线上了解" value="线上了解"></el-option>
                    <el-option label="业务人员拜访" value="业务人员拜访"></el-option>
                    <el-option label="其他" value="其他"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="购买渠道二（选填）" prop="purchaseChannel2">
                  <el-input v-model="form.purchaseChannel2" maxlength="50" clearable placeholder="可填写来源说明"></el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </section>

          <section class="form-section">
            <div class="section-head">
              <div>
                <h2>联系人与地址</h2>
                <p>固定电话选填，手机号将用于短信验证码和后续找回密码。</p>
              </div>
            </div>
            <el-row :gutter="18">
              <el-col :xs="24" :sm="12">
                <el-form-item label="联系人" prop="contactPerson">
                  <el-input v-model="form.contactPerson" maxlength="50" clearable></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="固定电话">
                  <el-input v-model="form.telephone" maxlength="50" clearable placeholder="选填"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="8">
                <el-form-item label="省份" prop="province">
                  <el-select v-model="form.province" placeholder="请选择省份" class="full-control" @change="handleProvinceChange">
                    <el-option v-for="region in regions" :key="region.name" :label="region.name" :value="region.name"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="8">
                <el-form-item label="城市" prop="city">
                  <el-select v-model="form.city" placeholder="请选择城市" class="full-control" :disabled="cityOptions.length === 0">
                    <el-option v-for="city in cityOptions" :key="city" :label="city" :value="city"></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="8">
                <el-form-item label="邮编">
                  <el-input v-model="form.zipcode" maxlength="6" clearable placeholder="默认000000"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24">
                <el-form-item label="详细地址" prop="address">
                  <el-input v-model="form.address" maxlength="50" clearable></el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </section>

          <section class="form-section submit-section">
            <el-checkbox v-model="agreementAccepted">我确认提交的信息和证照真实有效，并知晓注册后需等待审核。</el-checkbox>
            <div class="submit-actions">
              <el-button type="primary" :loading="submitLoading" @click="submitRegistration">提交注册</el-button>
            </div>
          </section>
        </el-form>
      </main>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import regions from '@/assets/data/regions'

const MAX_FILE_SIZE = 15 * 1024 * 1024
const IMAGE_TYPES = ['image/jpeg', 'image/png']
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']

export default {
  name: 'SchoolRegister',
  data () {
    return {
      regions,
      activeStep: 0,
      isMobileSteps: false,
      codeLoading: false,
      submitLoading: false,
      codeCountdown: 0,
      codeTimer: null,
      userNameAvailable: true,
      userNameMessage: '',
      schoolNameAvailable: true,
      schoolNameMessage: '',
      agreementAccepted: false,
      legacyLoginUrl: 'https://www.xinzhitang.com.cn/SchoolWorld/users/denglu.htm',
      files: {
        certpic1: { file: null, preview: '', ocrMessage: '待上传' },
        certpic2: { file: null, preview: '', ocrMessage: '待上传' }
      },
      form: {
        userName: '',
        password: '',
        confirmPassword: '',
        mobile: '',
        smsCode: '',
        schoolName: '',
        legalEntity: '',
        schoolSize: '',
        purchaseChannel1: '',
        purchaseChannel2: '',
        webAddress: '',
        contactPerson: '',
        telephone: '',
        province: '',
        city: '',
        address: '',
        zipcode: '000000'
      },
      rules: {
        userName: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { pattern: /^[A-Za-z0-9]{4,20}$/, message: '用户名需为4-20位英文字母或数字', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度需为6-20位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认密码', trigger: 'blur' },
          { validator: this.validateConfirmPassword, trigger: 'blur' }
        ],
        mobile: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1\d{10}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        smsCode: [
          { required: true, message: '请输入短信验证码', trigger: 'blur' },
          { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' }
        ],
        schoolName: [
          { required: true, message: '请输入学校名称', trigger: 'blur' }
        ],
        legalEntity: [
          { required: true, message: '请输入法人/负责人', trigger: 'blur' }
        ],
        schoolSize: [
          { required: true, message: '请选择学校规模', trigger: 'change' }
        ],
        purchaseChannel1: [
          { required: true, message: '请选择购买渠道', trigger: 'change' }
        ],
        purchaseChannel2: [],
        webAddress: [],
        contactPerson: [
          { required: true, message: '请输入联系人', trigger: 'blur' }
        ],
        province: [
          { required: true, message: '请选择省份', trigger: 'change' }
        ],
        city: [
          { required: true, message: '请选择城市', trigger: 'change' }
        ],
        address: [
          { required: true, message: '请输入详细地址', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    cityOptions () {
      const current = this.regions.find(item => item.name === this.form.province)
      return current ? current.cities : []
    },
    stepsDirection () {
      return this.isMobileSteps ? 'horizontal' : 'vertical'
    }
  },
  mounted () {
    this.updateStepsLayout()
    this.syncActiveStep()
    window.addEventListener('resize', this.updateStepsLayout)
  },
  beforeDestroy () {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
    }
    window.removeEventListener('resize', this.updateStepsLayout)
    this.revokePreview('certpic1')
    this.revokePreview('certpic2')
  },
  methods: {
    validateConfirmPassword (rule, value, callback) {
      if (value !== this.form.password) {
        callback(new Error('两次输入的密码不一致'))
        return
      }
      callback()
    },
    unwrapResponse (response) {
      return response && response.data && response.data.result ? response.data.result : response.data
    },
    handleProvinceChange () {
      this.form.city = ''
    },
    updateActiveStep (step) {
      this.activeStep = Math.max(this.calculateActiveStep(), step)
    },
    updateStepsLayout () {
      this.isMobileSteps = window.innerWidth <= 900
    },
    syncActiveStep () {
      this.activeStep = this.calculateActiveStep()
    },
    calculateActiveStep () {
      if (!this.isAccountStepComplete()) {
        return 0
      }
      if (!this.isCertificateStepComplete()) {
        return 1
      }
      if (!this.isSchoolStepComplete()) {
        return 2
      }
      if (!this.isContactStepComplete()) {
        return 3
      }
      return 4
    },
    isAccountStepComplete () {
      return /^[A-Za-z0-9]{4,20}$/.test(this.form.userName) &&
        this.userNameAvailable &&
        /^1\d{10}$/.test(this.form.mobile) &&
        this.form.password &&
        this.form.password.length >= 6 &&
        this.form.password.length <= 20 &&
        this.form.confirmPassword === this.form.password &&
        /^\d{6}$/.test(this.form.smsCode)
    },
    isCertificateStepComplete () {
      return !!(this.files.certpic1.file && this.files.certpic2.file)
    },
    isSchoolStepComplete () {
      return !!(this.form.schoolName &&
        this.schoolNameAvailable &&
        this.form.legalEntity &&
        this.form.schoolSize &&
        this.form.purchaseChannel1)
    },
    isContactStepComplete () {
      return !!(this.form.contactPerson &&
        this.form.province &&
        this.form.city &&
        this.form.address)
    },
    checkUserName () {
      if (!/^[A-Za-z0-9]{4,20}$/.test(this.form.userName)) {
        this.userNameMessage = ''
        this.syncActiveStep()
        return
      }
      this.$axios.get(api.schoolRegisterCheckUserName(this.form.userName)).then(response => {
        const result = this.unwrapResponse(response)
        this.userNameAvailable = !!result.available
        this.userNameMessage = result.message || ''
        this.syncActiveStep()
      }).catch(() => {
        this.userNameMessage = ''
        this.syncActiveStep()
      })
    },
    checkSchoolName () {
      if (!this.form.schoolName) {
        this.schoolNameMessage = ''
        this.syncActiveStep()
        return
      }
      this.$axios.get(api.schoolRegisterCheckSchoolName(this.form.schoolName)).then(response => {
        const result = this.unwrapResponse(response)
        this.schoolNameAvailable = !!result.available
        this.schoolNameMessage = result.message || ''
        this.syncActiveStep()
      }).catch(() => {
        this.schoolNameMessage = ''
        this.syncActiveStep()
      })
    },
    sendCode () {
      this.$refs.registerForm.validateField('mobile', error => {
        if (error) return
        this.codeLoading = true
        this.$axios.post(api.schoolRegisterSendCode, { mobile: this.form.mobile }).then(response => {
          const result = this.unwrapResponse(response)
          if (result.success) {
            this.$message.success(result.message || '验证码已发送')
            this.startCountdown()
            this.updateActiveStep(1)
          } else {
            this.$message.error(result.message || '验证码发送失败')
          }
        }).catch(error => {
          this.$message.error(this.getErrorMessage(error, '验证码发送失败'))
        }).finally(() => {
          this.codeLoading = false
        })
      })
    },
    startCountdown () {
      if (this.codeTimer) {
        clearInterval(this.codeTimer)
      }
      this.codeCountdown = 60
      this.codeTimer = setInterval(() => {
        if (this.codeCountdown <= 1) {
          clearInterval(this.codeTimer)
          this.codeTimer = null
          this.codeCountdown = 0
          return
        }
        this.codeCountdown--
      }, 1000)
    },
    handleFileChange (slot, uploadFile) {
      const raw = uploadFile.raw || uploadFile
      const error = this.validateFile(raw)
      if (error) {
        this.$message.error(error)
        return false
      }

      this.revokePreview(slot)
      this.files[slot].file = raw
      this.files[slot].preview = window.URL.createObjectURL(raw)
      this.files[slot].ocrMessage = '识别中...'
      this.updateActiveStep(2)
      this.callOcr(slot, raw)
      return false
    },
    validateFile (file) {
      if (!file) {
        return '请选择图片'
      }
      const extension = this.getFileExtension(file.name)
      if (IMAGE_TYPES.indexOf(file.type) === -1 && IMAGE_EXTENSIONS.indexOf(extension) === -1) {
        return '仅支持 JPG/JPEG/PNG 格式'
      }
      if (file.size > MAX_FILE_SIZE) {
        return '单张图片不能超过15M'
      }
      return ''
    },
    callOcr (slot, file) {
      this.$axios.post(api.schoolRegisterOcr, file, {
        params: {
          fileName: file.name || slot + '.jpg',
          imageType: file.type || '',
          certificateSlot: slot
        },
        headers: { 'Content-Type': 'application/octet-stream' }
      }).then(response => {
        const result = this.unwrapResponse(response)
        if (result && result.success) {
          this.applyOcrResult(result)
          this.files[slot].ocrMessage = result.message || '识别完成，可手工确认'
        } else {
          this.files[slot].ocrMessage = result && result.message ? result.message : '未识别，可手工填写'
        }
      }).catch(() => {
        this.files[slot].ocrMessage = '识别失败，可手工填写'
      })
    },
    applyOcrResult (result) {
      if (result.schoolName) this.form.schoolName = result.schoolName
      if (result.legalEntity) this.form.legalEntity = result.legalEntity
      if (result.address) this.form.address = result.address
      this.syncActiveStep()
    },
    removeFile (slot) {
      this.revokePreview(slot)
      this.files[slot].file = null
      this.files[slot].ocrMessage = '待上传'
    },
    revokePreview (slot) {
      if (this.files[slot].preview) {
        window.URL.revokeObjectURL(this.files[slot].preview)
        this.files[slot].preview = ''
      }
    },
    submitRegistration () {
      this.$refs.registerForm.validate(valid => {
        if (!valid) {
          this.$message.error('请先完善注册信息')
          return
        }
        if (!this.files.certpic1.file) {
          this.$message.error('请上传主体资质证件')
          return
        }
        if (!this.files.certpic2.file) {
          this.$message.error('请上传办学许可证')
          return
        }
        if (!this.agreementAccepted) {
          this.$message.error('请确认注册资料真实有效')
          return
        }

        this.submitLoading = true
        this.updateActiveStep(4)
        this.buildRegistrationPayload().then(payload => {
          return this.$axios.post(api.schoolRegisterSubmit, payload, {
            headers: { 'Content-Type': 'application/octet-stream' }
          })
        }).then(response => {
          const result = this.unwrapResponse(response)
          if (result.success) {
            this.$message.success(result.message || '注册成功')
            window.location.href = result.redirectUrl
          } else {
            this.applyFieldErrors(result)
            this.$message.error(result.message || '注册失败')
          }
        }).catch(error => {
          this.$message.error(this.getErrorMessage(error, '注册失败，请稍后重试'))
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    buildRegistrationPayload () {
      const certpic1 = this.files.certpic1.file
      const certpic2 = this.files.certpic2.file
      const metadata = {
        Input: {
          UserName: this.form.userName,
          Password: this.form.password,
          ConfirmPassword: this.form.confirmPassword,
          Mobile: this.form.mobile,
          SmsCode: this.form.smsCode,
          SchoolName: this.form.schoolName,
          LegalEntity: this.form.legalEntity,
          SchoolSize: this.form.schoolSize,
          PurchaseChannel1: this.form.purchaseChannel1,
          PurchaseChannel2: this.form.purchaseChannel2,
          ContactPerson: this.form.contactPerson,
          Telephone: this.form.telephone,
          WebAddress: this.form.webAddress,
          Province: this.form.province,
          City: this.form.city,
          Address: this.form.address,
          Zipcode: this.form.zipcode || '000000'
        },
        Certpic1FileName: certpic1.name || 'certpic1.jpg',
        Certpic1ContentType: certpic1.type || '',
        Certpic2FileName: certpic2.name || 'certpic2.jpg',
        Certpic2ContentType: certpic2.type || ''
      }
      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' })
      const header = new ArrayBuffer(16)
      const headerBytes = new Uint8Array(header)
      const headerView = new DataView(header)
      headerBytes.set([0x58, 0x5A, 0x54, 0x31], 0)
      headerView.setUint32(4, metadataBlob.size, true)
      headerView.setUint32(8, certpic1.size, true)
      headerView.setUint32(12, certpic2.size, true)
      return Promise.resolve(new Blob(
        [header, metadataBlob, certpic1, certpic2],
        { type: 'application/octet-stream' }
      ))
    },
    applyFieldErrors (result) {
      if (!result || !result.fieldErrors) return
      const firstError = Object.keys(result.fieldErrors)[0]
      if (firstError) {
        this.$message.error(result.fieldErrors[firstError])
      }
    },
    getFileExtension (fileName) {
      const index = fileName ? fileName.lastIndexOf('.') : -1
      return index >= 0 ? fileName.substring(index + 1).toLowerCase() : ''
    },
    getErrorMessage (error, fallback) {
      const data = error && error.response ? error.response.data : null
      const result = data && data.result ? data.result : data
      return result && result.message ? result.message : fallback
    }
  },
  watch: {
    form: {
      handler () {
        this.syncActiveStep()
      },
      deep: true
    },
    agreementAccepted () {
      this.syncActiveStep()
    },
    'files.certpic1.file' () {
      this.syncActiveStep()
    },
    'files.certpic2.file' () {
      this.syncActiveStep()
    }
  }
}
</script>

<style scoped>
.school-register-page {
  height: 100vh;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #eef4f8;
  color: #243447;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}

.topbar {
  min-height: 72px;
  padding: 14px 28px;
  background: #fff;
  border-bottom: 1px solid #dce5eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.brand {
  display: flex;
  align-items: center;
  min-width: 0;
}

.brand-mark {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #0f7f8c;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  margin-right: 12px;
  flex: 0 0 auto;
}

.brand-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.brand-subtitle {
  font-size: 13px;
  color: #6b7b88;
  margin-top: 3px;
}

.login-link {
  color: #0f7f8c;
  text-decoration: none;
  font-size: 14px;
}

.register-shell {
  width: min(1180px, calc(100% - 32px));
  margin: 24px auto calc(42px + env(safe-area-inset-bottom, 0px));
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  gap: 18px;
}

.progress-panel {
  background: #fff;
  border: 1px solid #dce5eb;
  border-radius: 8px;
  padding: 18px;
  align-self: start;
  position: sticky;
  top: 16px;
}

.progress-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 16px;
}

.steps {
  height: 360px;
}

.form-panel {
  background: #fff;
  border: 1px solid #dce5eb;
  border-radius: 8px;
  padding: 22px;
  min-width: 0;
}

.register-form {
  margin-top: 18px;
}

.register-form >>> .el-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.register-form >>> [class*="el-col-"] {
  float: none;
}

.form-section {
  border-top: 1px solid #e6edf2;
  padding: 24px 0 4px;
}

.form-section:first-of-type {
  border-top: none;
  padding-top: 4px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.section-head h2 {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
}

.section-head p {
  margin: 5px 0 0;
  color: #6b7b88;
  font-size: 13px;
}

.full-control {
  width: 100%;
}

.field-hint {
  font-size: 12px;
  color: #2b8a3e;
  margin-top: 4px;
  line-height: 18px;
}

.field-hint.danger {
  color: #c0392b;
}

.code-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px;
  gap: 10px;
}

.upload-box {
  min-height: 260px;
  border: 1px dashed #b9c9d6;
  border-radius: 8px;
  padding: 16px;
  background: #f8fbfd;
  box-sizing: border-box;
}

.upload-box.filled {
  border-style: solid;
  background: #fbfdff;
}

.upload-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.upload-title span {
  font-weight: 700;
  font-size: 15px;
}

.upload-title small {
  color: #6b7b88;
}

.preview-wrap {
  height: 160px;
  border-radius: 6px;
  overflow: hidden;
  background: #eef4f8;
  margin-bottom: 12px;
  position: relative;
}

.preview-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.remove-file {
  position: absolute;
  top: 8px;
  right: 8px;
  border: none;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  padding: 5px 8px;
  cursor: pointer;
}

.ocr-state {
  color: #6b7b88;
  font-size: 12px;
  margin-top: 10px;
  line-height: 18px;
}

.submit-section {
  padding-bottom: 0;
}

.submit-section >>> .el-checkbox {
  display: flex;
  align-items: flex-start;
  max-width: 100%;
  white-space: normal;
  line-height: 20px;
}

.submit-section >>> .el-checkbox__input {
  flex: 0 0 auto;
  margin-top: 2px;
}

.submit-section >>> .el-checkbox__label {
  flex: 1;
  min-width: 0;
  padding-left: 8px;
  white-space: normal;
  line-height: 20px;
  color: #5f7282;
}

.submit-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
}

.submit-actions .el-button {
  width: 160px;
}

.school-register-page >>> .el-form-item__label {
  line-height: 22px;
  padding-bottom: 7px;
  color: #374b5c;
}

.school-register-page >>> .el-input__inner,
.school-register-page >>> .el-textarea__inner {
  height: 44px;
  line-height: 44px;
  background-color: #fff !important;
  border-color: #cfdbe5 !important;
  color: #243447 !important;
  box-shadow: none !important;
  -webkit-text-fill-color: #243447 !important;
}

.school-register-page >>> .el-textarea__inner {
  height: auto;
  line-height: 20px;
}

.school-register-page >>> .el-input__inner::placeholder,
.school-register-page >>> .el-textarea__inner::placeholder {
  color: #8c9bab !important;
  -webkit-text-fill-color: #8c9bab !important;
}

.school-register-page >>> .el-input__inner:hover,
.school-register-page >>> .el-textarea__inner:hover {
  border-color: #9fb3c3 !important;
}

.school-register-page >>> .el-input__inner:focus,
.school-register-page >>> .el-textarea__inner:focus {
  border-color: #0f7f8c !important;
}

.school-register-page >>> .el-input.is-disabled .el-input__inner,
.school-register-page >>> .el-select .el-input.is-disabled .el-input__inner {
  background-color: #f4f7fa !important;
  color: #7d8c99 !important;
  -webkit-text-fill-color: #7d8c99 !important;
}

.school-register-page >>> .el-input__icon {
  color: #8c9bab !important;
  line-height: 44px;
}

.school-register-page >>> input:-webkit-autofill,
.school-register-page >>> input:-webkit-autofill:hover,
.school-register-page >>> input:-webkit-autofill:focus {
  box-shadow: 0 0 0 1000px #fff inset !important;
  -webkit-text-fill-color: #243447 !important;
  caret-color: #243447;
}

.school-register-page >>> .el-select-dropdown,
.school-register-page >>> .el-popper {
  background-color: #fff !important;
  border-color: #dce5eb !important;
}

.school-register-page >>> .el-select-dropdown__item {
  color: #243447 !important;
}

.school-register-page >>> .el-select-dropdown__item.hover,
.school-register-page >>> .el-select-dropdown__item:hover {
  background-color: #eef7f9 !important;
}

.school-register-page >>> .el-button {
  border-radius: 6px;
}

@media (max-width: 900px) {
  .register-shell {
    grid-template-columns: 1fr;
  }

  .progress-panel {
    position: static;
    padding-bottom: 14px;
  }

  .steps {
    height: auto;
  }

  .school-register-page >>> .steps.el-steps--horizontal {
    display: flex;
    align-items: flex-start;
  }

  .school-register-page >>> .steps.el-steps--horizontal .el-step {
    flex: 1;
    min-width: 0;
  }

  .school-register-page >>> .steps.el-steps--horizontal .el-step__head {
    display: flex;
    justify-content: center;
  }

  .school-register-page >>> .steps.el-steps--horizontal .el-step__main {
    text-align: center;
    padding-top: 8px;
    white-space: normal;
  }

  .school-register-page >>> .steps.el-steps--horizontal .el-step__title {
    font-size: 13px;
    line-height: 18px;
    white-space: nowrap;
  }

  .school-register-page >>> .steps.el-steps--horizontal .el-step__line {
    top: 12px;
  }
}

@media (max-width: 640px) {
  .topbar {
    padding: 12px 16px;
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .brand-title {
    font-size: 18px;
  }

  .register-shell {
    width: calc(100% - 20px);
    margin-top: 12px;
    margin-bottom: calc(32px + env(safe-area-inset-bottom, 0px));
  }

  .form-panel,
  .progress-panel {
    padding: 14px;
  }

  .progress-title {
    margin-bottom: 14px;
  }

  .register-form {
    margin-top: 14px;
  }

  .school-register-page >>> .steps.el-steps--horizontal .el-step__title {
    font-size: 12px;
  }

  .school-register-page >>> .el-form-item {
    margin-bottom: 18px;
  }

  .code-line {
    grid-template-columns: 1fr;
  }

  .submit-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .submit-actions .el-button {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/view/auth/SchoolRegister.vue